import React, { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import bem from 'utils/bem';
import { useMobile } from 'utils/media';
import MobileSlider from 'components/Slider/MobileSlider';
import useCart, { tryParse } from 'components/CartWidget/useCart';
import Details from 'components/Details';
import Text from 'components/Text';
import ShopItemTeaser, { ShopItemTeaserSkeleton } from 'components/ShopItemTeaser';
import Page from '../../Page';
import PhotoGallery, { PhotoGallerySkeleton } from '../PhotoGallery';
import CartButton from '../CartButton';
import Model from '../../../../model';
import TeaserPhoto from 'components/TeaserPhoto';
import { times } from 'utils/render';
import './index.sass';
import { Skeleton } from '@mui/material';
import { computed } from 'mobx';

const { block, el } = bem('shop-item');
const Item = () => {
    const { name: slug } = useParams();

    const { itemsSampleData, oneItemData } = Model.shop;

    const shopItemData = useMemo(() => oneItemData(slug), [slug]);
    const teasersData = itemsSampleData([slug]);

    return (
        <Page className={ block() } >
            <Suspense fallback={<ContentBlockSkeleton />}>
                <ContentBlock resource={shopItemData}/>
            </Suspense>
            <TeasersBlock allItemsData={teasersData} slug={slug} />
        </Page>
    )
}

const ContentBlock = ({resource}) => {
    const { isMobile } = useMobile();
    const shopItem = resource.read();

    const photos = useMemo(() => shopItem.data.files, [shopItem])
    const rPhotos = isMobile ?
        <MobileSlider items={ photos.map(image => <TeaserPhoto src={ image.fileResized }/>) }/> :
        <PhotoGallery items={ photos.map(image => ({
            original: `/media/${ image.file }`,
            thumbnail: `/media/${ image.fileResized }`
        })) }/>

    return (
        <div className={ el('top-block') }>
            <div className={ el('photo-block') }>
                { rPhotos }
            </div>
            <div className={ el('description-block') }>
                <div className={ el('title') }>
                    <Text serif>{ shopItem.data.name }</Text>
                </div>
                <div className={ el('short-description') }>
                    <Text>{ shopItem.data.descriptionShort }</Text>
                </div>
                <div className={ el('full-description') }>
                    <Details>
                        <Text tag={'div'}>{ shopItem.data.descriptionFull }</Text>
                    </Details>
                </div>
                <div className={ el('separator') }/>
                <div className={ el('price-block') }>
                    <PriceBlock slug={ shopItem.data.url } />
                </div>
            </div>
        </div>
    )
}

const PriceBlock = observer(({slug}) => {

    const { getShopItemBySlug } = Model.shop;
    const shopItem = computed(() => {
        return getShopItemBySlug(slug)
    }).get();

    const [ amount, setAmount ] = useState(0);
    const { cart } = useCart();

    useEffect(() => {
        if (!cart || !shopItem) {
            setAmount(0);
            return;
        }
        const currentItem = tryParse(cart).find(item => item.id === shopItem.id)
        if (currentItem) {
            return setAmount(currentItem.amount);
        } else {
            setAmount(0)
        }
    }, [ cart, shopItem ]);


    return (
        <>
            <div className={ el('price') }>
                <Text serif>{ shopItem.price } руб.</Text>
            </div>
            <div className={ el('cart-button') }>
                <CartButton id={ shopItem.id } amount={ amount } maxAmount={ shopItem.data.amount } setAmount={ setAmount }/>
            </div>
        </>
    )
})

const ContentBlockSkeleton = () => {
    const { isMobile } = useMobile();
    const Photos = isMobile ?
        <MobileSlider items={ times((key) => <TeaserPhoto key={key} />) } /> :
        <PhotoGallerySkeleton />
    const Description = times((key) => <Skeleton key={key} />, 3);

    return (
        <div className={ el('top-block') }>
            <div className={ el('photo-block') }>
                { Photos }
            </div>
            <div className={ el('description-block') }>
                <div className={ el('title') }>
                    <Text serif><Skeleton /></Text>
                </div>
                <div className={ el('short-description') }>
                    <Text>{Description}</Text>
                </div>
                <div className={ el('separator') }/>
                <div className={ el('price-block') }>
                    <div className={ el('price') }>
                        <Text serif><Skeleton width={83}/></Text>
                    </div>
                    <div className={ el('cart-button') }>
                        <Skeleton width={173} variant={'rounded'} />
                    </div>
                </div>
            </div>
        </div>
    )
}

const TeasersBlock = memo(({slug, allItemsData}) => {
    const { isMobile } = useMobile();

    const SamplesFallback = isMobile ?
        <MobileSlider items={times((key) => <ShopItemTeaserSkeleton key={key} />, 2)} /> :
        times((key) => <ShopItemTeaserSkeleton key={key} />, 4)

    return (
        <div className={ el('teasers-block') }>
            <div className={ el('teasers-block-title') }>
                <Text serif>Вас может заинтересовать</Text>
            </div>
            <div className={ el('teasers-block-list') }>
                <Suspense fallback={ SamplesFallback }>
                    <SamplesBlock resource={allItemsData} isMobile={isMobile}/>
                </Suspense>
            </div>
        </div>
    )
})

const SamplesBlock = memo(({resource, isMobile}) => {
    const samples = resource.read();
    const rSamples = samples.map((item, index) => <ShopItemTeaser key={ index } item={ item }/>);
    return isMobile ? <MobileSlider items={rSamples} /> : rSamples
})

export default observer(Item);
